import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { corporateCMSApi } from "~/Services";

interface parentHeader {
  id: number;
  name: string;
  field_footer_main_name_ms: string;
  field_footer_main_name_zh: string;
  field_footer_main_key_en: string;
  field_footer_main_key_ms: string;
  field_footer_main_key_zh: string;
  field_footer_menu_redirect_url_e: string;
  field_footer_menu_redirect_url_m: string;
  field_footer_menu_redirect_url_z: string;
  published: boolean;
  field_footer_main_menu_created_a: string;
  field_footer_main_menu_updated_a: string;
}

interface WebAppFooter {
  id: number;
  title: string;
  field_copyright_text_en: string;
  field_copyright_text_ms: string;
  field_copyright_text_zh: string;
  field_registration_number_txt_en: string;
  field_registration_number_txt_ms: string;
  field_registration_number_txt_zh: string;
  field_mobile_app_text_en: string;
  field_mobile_app_text_ms: string;
  field_mobile_app_text_zh: string;
  field_term_of_use_text_en: string;
  field_term_of_use_text_ms: string;
  field_term_of_use_text_zh: string;
  termsOfUseLink: string;
  field_privacy_policy_text_en: string;
  field_privacy_policy_text_ms: string;
  field_privacy_policy_text_zh: string;
  privacyPolicyLink: string;
  playStoreImage: string;
  playStoreLink: string;
  appStoreImage: string;
  appStoreLink: string;
  logos: {
    id: number;
    name: string;
    image: string;
    link: string;
    isSocialMedia: boolean;
    published: boolean;
    createdAt: string;
    updatedAt: string;
  }[];
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface GrandChildHeader {
  id: number;
  name: string;
  field_footer_grand_child_name: string;
  field_footer_grand_child_name_zh?: string;
  field_footer_grand_child_key_en?: string;
  field_footer_grand_child_key_ms?: string;
  field_footer_grand_child_key_zh?: string;
  field_footer_grand_redirect_en?: string;
  field_footer_grand_redirect_ms?: string;
  field_footer_grand_redirect_zh?: string;
  child_heading_id: string;
  published: boolean;
  field_footer_grand_created_at: string;
  field_footer_grant_updated_at: string;
}

interface ChildHeader {
  tid: number;
  name: string;
  field_footer_child_name_ms?: string;
  field_footer_child_name_zh?: string;
  field_footer_child_key_en: string;
  field_footer_child_key_ms?: string;
  field_footer_child_key_zh?: string;
  field_footer_child_redirect_url?: string;
  field_footer_child_redirect_ms?: string;
  field_footer_child_redirect_zh?: string;
  parent_id: string;
  published: boolean;
  field_footer_child_created_at: string;
  field_footer_child_updated_at: string;
}

const useParentHeader = (
  id: string,
  options?: UseQueryOptions<parentHeader, DefaultError, parentHeader, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<parentHeader, DefaultError> => {
  return useQuery<parentHeader, DefaultError, parentHeader, QueryKey>(
    {
      queryKey: ["parentHeader", id],
      enabled: false,
      queryFn: async (): Promise<parentHeader> => {
        const response = await corporateCMSApi.get<any>(`/api/get-footer-main-menu-id/${id}`);
        return response.data[0];
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useChildeader = (
  id: string,
  options?: UseQueryOptions<ChildHeader, DefaultError, ChildHeader, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<ChildHeader, DefaultError> => {
  return useQuery<ChildHeader, DefaultError, ChildHeader, QueryKey>(
    {
      queryKey: ["childHeader", id],
      enabled: false,
      queryFn: async (): Promise<ChildHeader> => {
        const response = await corporateCMSApi.get<any>(`/api/get-footer-child-menu-id/${id}`);
        return response.data[0];
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useGrandChildeader = (
  id: string,
  options?: UseQueryOptions<GrandChildHeader, DefaultError, GrandChildHeader, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<GrandChildHeader, DefaultError> => {
  return useQuery<GrandChildHeader, DefaultError, GrandChildHeader, QueryKey>(
    {
      queryKey: ["grandChildHeader", id],
      enabled: false,
      queryFn: async (): Promise<GrandChildHeader> => {
        const response = await corporateCMSApi.get<any>(`/api/get-footer-grand-child-menu-id/${id}`);
        return response.data[0];
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWebAppFooter = (
  options?: UseQueryOptions<WebAppFooter, DefaultError, WebAppFooter, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WebAppFooter, DefaultError> => {
  return useQuery<WebAppFooter, DefaultError, WebAppFooter, QueryKey>(
    {
      queryKey: ["webAppFooter"],
      enabled: false,
      queryFn: async (): Promise<WebAppFooter> => {
        const response = await corporateCMSApi.get<any>(`/api/web-app-footer/allfields`);
        return response.data[0];
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export { useChildeader, useGrandChildeader, useParentHeader, useWebAppFooter };
